/**
 * VERY IMPORTANT: the ResponsiveTable component is designed to
 * only work with the useReactTable hook from @tanstack/react-table,
 * which should be declared on the parent component.
 *
 * The hook is not included on the component since it is a custom hook that needs to be implemented
 * on the parent component with rules that can be defined for each specific case.
 *
 * @param {Object} props.table - The table instance created by useReactTable.
 *
 * @param {string} props.globalFilter - The global filter applied to the table.
 *
 * @param {Function} props.setGlobalFilter - Function to update the global filter.
 *
 * @param {boolean} props.loading - Indicates if the data is being loaded.
 *
 * @param {boolean} props.hasTabs - Indicates if the table should display tabs.
 *
 * @param {Array} props.tabs - The list of tabs to be displayed.
 *
 * @param {string} props.activeTab - The currently active tab.
 *
 * @param {Function} props.setActiveTab - Function to update the active tab.
 *
 * @param {boolean} props.hasFilter - Indicates if the table should display the filter.
 *
 * @param {boolean} props.hasFooter - Indicates if the table should display the footer.
 * The footer shows the total items on the table and allows setting the amount of items per page.
 *
 * @param {ReactNode} props.filters - The filter components to be displayed in the dropdown.
 * Allows to add custom filter components on the property where the ResponsiveTable is being used.
 *
 * @param {number} props.totalPages - The total number of pages available.
 *
 * @param {number} props.totalItems - The total number of items available.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Filter,
  Search
} from "feather-icons-react/build/IconComponents";
import { flexRender } from "@tanstack/react-table";
import debounce from "helpers/debounce";
import CustomLoader from 'components/CustomLoader';

export default function ResponsiveTable({
  table,
  globalFilter,
  setGlobalFilter,
  loading,
  hasTabs,
  tabs,
  activeTab,
  setActiveTab,
  hasFilter,
  hasFooter,
  filters,
  totalPages,
  totalItems,
}) {
  const [searchQuery, setSearchQuery] = useState(globalFilter);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef(null);

  const debouncedSetGlobalFilter = debounce((query) => {
    setGlobalFilter(query);
  }, 1000);

  useEffect(() => {
    debouncedSetGlobalFilter(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleFilterButtonClick = () => {
    setIsFilterDropdownOpen(!isFilterDropdownOpen);
  };

  const handleClickOutside = (event) => {
    if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
      setIsFilterDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      {hasTabs && (
        <div className="flex ml-4 space-x-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`
                px-4 py-2 rounded-t-lg
                ${activeTab === tab.id
                  ? 'bg-white text-gray-500 font-semibold'
                  : 'bg-gray-200 text-gray-500'
                }`
              }
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      <div
        className="
      flex flex-col mx-auto pt-2 px-4 sm:px-4
      lg:px-4 bg-white rounded-2xl shadow-md
      "
      >
        <div
          className="bg-white flex flex-col sm:flex-row
          justify-between items-center mb-2.5
          text-sm text-gray-500 rounded-2xl pt-1"
        >
          {/* Search */}
          <div className={`relative w-full ${hasFilter ? 'sm:w-3/5' : 'sm:w-3/4'} m-2`}>
            <input
              value={searchQuery ?? ""}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="
              w-full pl-10 pr-4 py-2 border
              border-gray-400 rounded-xl font-semibold
              focus:ring-gray-100 focus:border-gray-500
              text-gray-500 bg-white
            "
            />
            <Search
              className="
              absolute left-3 top-1/2 transform
              -translate-y-1/2 text-gray-500"
              size={20}
            />
          </div>

          {/* Filters */}
          {hasFilter && (
            <div className="relative w-full sm:w-1/5 m-1" ref={filterDropdownRef}>
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                <Filter size={20} />
              </div>
              <button
                className="
                w-full pl-10 pr-4 py-2 border
                border-gray-400 rounded-xl font-semibold
                text-gray-500 cursor-pointer bg-white
                flex items-center justify-between"
                onClick={handleFilterButtonClick}
              >
                Filters
                {isFilterDropdownOpen ? (
                  <ChevronUp className="ml-2" size={20} />
                ) : (
                  <ChevronDown className="ml-2" size={20} />
                )}
              </button>
              {isFilterDropdownOpen && (
                <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg">
                  <div className="h-auto overflow-y-auto overflow-x-visible">
                    {filters}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          <div className={`relative w-full ${hasFilter ? 'sm:w-1/5' : 'sm:w-1/4'} m-1 flex items-center space-x-2 sm:border-l sm:border-gray-300 sm:pl-2`}>
            <div className="flex w-full justify-between items-center">
              <button
                className="
                p-2 rounded-xl bg-gray-100
                text-gray-500 hover:bg-gray-200
                disabled:opacity-50"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronLeft size={20} />
              </button>

              <span className="flex items-center">
                <p className="mr-1 text-md">page</p>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={table.getState().pagination.pageIndex + 1}
                  onChange={(e) => {
                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                    table.setPageIndex(page);
                  }}
                  className="
                  w-16 p-2 rounded-xl border
                  border-gray-100 text-center"
                />
                <span className="ml-1">of {totalPages}</span>
              </span>

              <button
                className="
                p-2 rounded-xl bg-gray-100
                text-gray-500 hover:bg-gray-200
                disabled:opacity-50"
                onClick={() => table.nextPage()}
                disabled={
                  table.getState().pagination.pageIndex === totalPages - 1
                }
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <CustomLoader />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200 p-2 mb-4">
              <div className="max-h-[650px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="sticky top-0 bg-white z-10 border-b border-gray-200">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id} className="bg-white">
                        {headerGroup.headers.map((header) => (
                          <th
                            className="
                            bg-white border-b border-gray-200
                            px-4 text-left font-semibold
                          "
                            key={header.id}
                          >
                            <div
                              {...{
                                className: header.column.getCanSort()
                                  ? "cursor-pointer select-none flex items-center"
                                  : "",
                                onClick: header.column.getToggleSortingHandler(),
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {table.getRowModel().rows.map(row => (
                      <tr
                        key={row.id}
                        className={`border-b ${row.getIsSelected() ? 'bg-red-100' : ''}`}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="
                            px-5 py-3 whitespace-nowrap
                            text-md text-gray-500
                          "
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              {
                                ...cell.getContext(),
                                isSelected: row.getIsSelected(),
                              }
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {hasFooter && (
              <div className="flex items-center justify-between mb-4 pb-2 sm:mb-0">
                <div className="flex items-center">
                  <span className="mr-2">Items per page</span>
                  <select
                    className="
                    border bg-white border-gray-300
                    rounded-md shadow-sm p-2"
                    value={table.getState().pagination.pageSize}
                    onChange={(e) => {
                      table.setPageSize(Number(e.target.value));
                    }}
                  >
                    {[10, 20, 30, 50, 100].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <span>Total items: {totalItems}</span>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
